import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router";
import { listMyExperiments, listTeamExperiments, type TrainingExperiment } from "@/api";
import {
  ALL_SESSION_FILTER,
  memberSearchParamKey,
  readPolicyFilterParams,
  type SessionScope,
} from "@/modules/training/public";
import {
  clearLegacyWorkspaceParam,
  hasLegacyWorkspaceParam,
  writeExperimentParam,
  writeMemberParam,
} from "./policyFilterParams";
import { useAsyncList } from "./useAsyncList";
import { useTeamMembers } from "./useTeamMembers";

// Evaluation Policy 카탈로그의 member→experiment 조회 축. Me 는 멤버 선택 없이 actor 의 workspace
// 합집합을 받는다. Team 만 ?member= 를 BE 의 member_uuid 로 옮긴다. Results 창은 열 때 이 선택을
// 스냅샷으로 받으므로 자체 조회 축을 소유하지 않는다.
export type MemberExperimentSelection = {
  memberUuid: string;
  experimentId: string;
  memberOptions: readonly { value: string; label: string }[];
  experimentOptions: readonly { value: string; label: string }[];
  onMemberChange: (next: string) => void;
  onExperimentChange: (next: string) => void;
};

export function useMemberExperimentSelection(
  scope: SessionScope,
  groupUuid: string | null,
): MemberExperimentSelection {
  const [searchParams, setSearchParams] = useSearchParams();
  const { memberUuid, experimentId } = readPolicyFilterParams(searchParams);
  const members = useTeamMembers(scope === "team", groupUuid);

  const experimentsFetcher = useCallback(() => {
    if (scope === "team" && groupUuid === null) {
      return Promise.resolve<readonly TrainingExperiment[]>([]);
    }
    return scope === "me"
      ? listMyExperiments()
      : listTeamExperiments(
          memberUuid === ALL_SESSION_FILTER ? undefined : memberUuid,
          groupUuid ?? undefined,
        );
  }, [scope, memberUuid, groupUuid]);
  const experiments = useAsyncList<TrainingExperiment>(experimentsFetcher);

  const onMemberChange = useCallback(
    (next: string) => {
      setSearchParams((prev) => writeMemberParam(prev, next), { replace: true });
    },
    [setSearchParams],
  );
  const onExperimentChange = useCallback(
    (next: string) => {
      setSearchParams((prev) => writeExperimentParam(prev, next), { replace: true });
    },
    [setSearchParams],
  );

  // URL 정규화는 replace 커밋 한 번으로 끝낸다. 이전 workspace 딥링크는 새 축을 바꾸지 않는다. Me 에
  // 남은 member 값은 지우되 실험은 유지한다. Team 의 무효 멤버만 하위 실험과 정책 선택을 함께 지운다.
  // 그룹과 옵션 후보가 확정되기 전에는 외부 입력을 무효라고 판정하지 않는다.
  useEffect(() => {
    const staleMeMember = scope === "me" && memberUuid !== ALL_SESSION_FILTER;
    const invalidTeamMember =
      scope === "team" &&
      groupUuid !== null &&
      memberUuid !== ALL_SESSION_FILTER &&
      members.status === "success" &&
      !members.items.some((member) => member.uuid === memberUuid);
    const invalidExperiment =
      experimentId !== ALL_SESSION_FILTER &&
      experiments.status === "success" &&
      !experiments.items.some((experiment) => experiment.id === experimentId);
    if (
      !hasLegacyWorkspaceParam(searchParams) &&
      !staleMeMember &&
      !invalidTeamMember &&
      !invalidExperiment
    ) {
      return;
    }
    setSearchParams(
      (prev) => {
        const next = clearLegacyWorkspaceParam(prev);
        if (invalidTeamMember) {
          return writeMemberParam(next, ALL_SESSION_FILTER);
        }
        if (staleMeMember) {
          next.delete(memberSearchParamKey);
        }
        return invalidExperiment ? writeExperimentParam(next, ALL_SESSION_FILTER) : next;
      },
      { replace: true },
    );
  }, [
    scope,
    groupUuid,
    memberUuid,
    experimentId,
    members.status,
    members.items,
    experiments.status,
    experiments.items,
    searchParams,
    setSearchParams,
  ]);

  // 첫 항목은 facet 드롭다운의 중립값이다. Training 세션 필터와 같은 "전체" 어휘를 쓴다.
  const memberOptions = useMemo(
    () => [
      { value: ALL_SESSION_FILTER, label: "전체" },
      ...members.items.map((member) => ({ value: member.uuid, label: member.display_name })),
    ],
    [members.items],
  );
  const experimentOptions = useMemo(
    () => [
      { value: ALL_SESSION_FILTER, label: "전체" },
      ...experiments.items.map((experiment) => ({ value: experiment.id, label: experiment.name })),
    ],
    [experiments.items],
  );

  return {
    memberUuid,
    experimentId,
    memberOptions,
    experimentOptions,
    onMemberChange,
    onExperimentChange,
  };
}
